import { useQuery } from "@tanstack/react-query";

import useUser from "@/api/apis";
import {
  parseTradeInCampaign,
  parseTradeInClaims,
  parseTradeInIssue,
  parseTradeInVerification,
  toTradeInOutcome,
  type TradeInCampaign,
  type TradeInClaim,
  type TradeInConsents,
  type TradeInIssue,
  type TradeInOutcome,
  type TradeInVerification,
} from "@/libs/trade-in/trade-in";

/**
 * 보상판매(#6101) — mgmt `/api/v1/trade-in/*` 호출.
 *
 * 브라우저가 `/admin-api/*` 리라이트로 부른다(`CouponApi.ts` 와 같은 패턴). 그래서 이 기능에는 **웹 서버 라우트가 없다** —
 * prod 에 새로 열리는 경로가 없고, 노출은 mgmt 의 캠페인 상태(`state`)와 `/trade-in/` 페이지 잠금이 정한다.
 * 결과는 던지지 않고 [TradeInOutcome] 하나로 돌려준다 — 해석 규칙은 `libs/trade-in/trade-in.ts`.
 */

const BASE = "/admin-api/api/v1/trade-in";

async function call<T>(
  path: string,
  init: RequestInit,
  parse: (message: unknown) => T | null,
): Promise<TradeInOutcome<T>> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/${path}/`, init);
  } catch {
    return { kind: "error", status: null, reason: "network" };
  }
  // 오류 응답은 JSON 이 아닐 수 있다(엣지의 HTML 403 등) — 본문을 못 읽어도 상태로 가른다.
  const body = await res.json().catch(() => null);
  return toTradeInOutcome(
    res.status,
    body,
    res.headers.get("retry-after"),
    parse,
  );
}

const authHeaders = (accessToken: string) => ({
  authorization: "Bearer " + accessToken,
  "content-type": "application/json",
});

/** 캠페인 — 공개. 로그인 전에도 상태(시작 전·진행·중단·종료)와 기간을 그린다. */
export function fetchTradeInCampaign(): Promise<
  TradeInOutcome<TradeInCampaign>
> {
  return call("campaign", { method: "GET" }, parseTradeInCampaign);
}

/**
 * 시리얼 확인. 입력값은 **trim 만** 한다 — 표시형·16진·대소문자·하이픈 정규화는 서버 몫이고, O/0·I/1 은 서로 다른 문자라
 * 보정하지 않는다(계약 v0). 시리얼은 본문에만 싣는다.
 */
export function verifyTradeInSerial(
  serialNumber: string,
  accessToken: string,
): Promise<TradeInOutcome<TradeInVerification>> {
  return call(
    "verifications",
    {
      method: "POST",
      headers: authHeaders(accessToken),
      body: JSON.stringify({ serialNumber: serialNumber.trim() }),
    },
    parseTradeInVerification,
  );
}

/**
 * 쿠폰 발급. `termsVersion` 은 화면에 보인 캠페인의 값을 그대로 싣는다(다르면 `TERMS_OUTDATED` — 약관을 다시 불러 보인다).
 * 같은 토큰으로 다시 부르면 서버가 새로 만들지 않고 `EXISTING_CLAIM` 을 돌려준다 — 통신이 끊겼으면 그냥 다시 부르면 된다.
 */
export function issueTradeInCoupon(
  request: {
    verificationToken: string;
    consents: TradeInConsents;
    termsVersion: string;
    locale: string;
  },
  accessToken: string,
): Promise<TradeInOutcome<TradeInIssue>> {
  return call(
    "claims",
    {
      method: "POST",
      headers: authHeaders(accessToken),
      body: JSON.stringify(request),
    },
    parseTradeInIssue,
  );
}

/** 내 참여 내역(최신순) — 새로고침·재로그인·다른 기기에서 신청 상태를 복구한다. */
export function fetchMyTradeInClaims(
  accessToken: string,
): Promise<TradeInOutcome<TradeInClaim[]>> {
  return call(
    "claims",
    { method: "GET", headers: authHeaders(accessToken) },
    parseTradeInClaims,
  );
}

export function useTradeInCampaign() {
  return useQuery({
    queryKey: ["trade-in", "campaign"],
    // 상태(OPEN·PAUSED)는 운영이 바꾼다 — 짧게 잡는다. 발급 직전엔 서버가 다시 본다.
    staleTime: 30_000,
    queryFn: fetchTradeInCampaign,
  });
}

/** 게스트는 조회하지 않는다 — 신청은 계정에 붙는다. */
export function useMyTradeInClaims() {
  const { token } = useUser();
  const accessToken = token?.token;

  return useQuery({
    queryKey: ["trade-in", "claims", accessToken ? "auth" : "guest"],
    enabled: !!accessToken,
    staleTime: 0,
    queryFn: () => fetchMyTradeInClaims(accessToken!),
  });
}
